import React, { useState, useEffect, useMemo } from 'react'
import { MagnifyingGlass, User, Buildings, Briefcase } from '@phosphor-icons/react'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { DotLoader } from '@/components/ui/DotLoader'
import { api } from '@/lib/api'

interface Contact {
  id: number
  email: string
  first_name?: string
  last_name?: string
  full_name?: string
  job_title?: string
  company_name?: string
}

interface Props {
  onSelect: (contactId: number, contactName: string) => void
}

// Turn an email local-part into a readable name, e.g. "niladri.c" -> "Niladri C".
const humanizeEmailLocal = (email: string): string => {
  const local = (email || '').split('@')[0] || ''
  const tokens = local.split(/[._\-+]+/).filter(Boolean)
  if (tokens.length === 0) return email
  return tokens.map(t => t.charAt(0).toUpperCase() + t.slice(1)).join(' ')
}

// Always resolves to a non-empty, human-readable name. Falls back to a
// humanized email local-part so a contact with no name fields never shows a
// blank line or the raw email as its "name".
const resolveDisplayName = (c: Contact): string => {
  if (c.full_name && c.full_name.trim()) return c.full_name.trim()
  const joined = [c.first_name, c.last_name].filter(Boolean).join(' ').trim()
  if (joined) return joined
  return humanizeEmailLocal(c.email)
}

const initialsOf = (name: string, email: string): string => {
  const tokens = name.split(/\s+/).filter(Boolean)
  if (tokens.length >= 2) return (tokens[0][0] + tokens[1][0]).toUpperCase()
  if (tokens.length === 1 && tokens[0]) return tokens[0].slice(0, 2).toUpperCase()
  return (email[0] || '?').toUpperCase()
}

export const PlaygroundContactSelector: React.FC<Props> = ({ onSelect }) => {
  const [contacts, setContacts] = useState<Contact[]>([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/contacts/', { params: { limit: 200, skip: 0 } })
        setContacts(res.data)
      } catch {
        // ignore
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  const filtered = useMemo(() => {
    if (!search.trim()) return contacts
    const q = search.toLowerCase()
    return contacts.filter(c =>
      (c.email || '').toLowerCase().includes(q) ||
      (c.full_name || '').toLowerCase().includes(q) ||
      (c.first_name || '').toLowerCase().includes(q) ||
      (c.last_name || '').toLowerCase().includes(q) ||
      (c.company_name || '').toLowerCase().includes(q) ||
      (c.job_title || '').toLowerCase().includes(q)
    )
  }, [contacts, search])

  return (
    <div className="flex flex-col items-center h-full overflow-y-auto px-8 pt-14 pb-8 max-w-lg mx-auto">
      <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6 bg-[#5e6ad2]/10 text-[#5e6ad2] dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc]">
        <User className="w-8 h-8" />
      </div>
      <h2 className="text-xl font-emphasis text-foreground mb-2" style={{ letterSpacing: '-0.24px' }}>Select a Contact</h2>
      <p className="text-sm text-muted-foreground mb-6 text-center">
        Choose a contact to simulate the workflow for. AI-generated emails will be personalized to this contact.
      </p>

      <Input
        placeholder="Search by name, email, or company..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        icon={<MagnifyingGlass className="w-4 h-4" />}
        className="mb-3 w-full"
      />

      {!loading && contacts.length > 0 && (
        <div className="w-full mb-2 flex items-center px-1">
          <span className="text-xs text-muted-foreground">
            {filtered.length} {filtered.length === 1 ? 'contact' : 'contacts'}
          </span>
        </div>
      )}

      {/* AB#21891 — this card sits in a flex-column that can itself run out of
          room (root has h-full overflow-y-auto). Without flex-shrink-0, a flex
          item with overflow-hidden gets an automatic min-height of 0, so under
          space pressure the flexbox algorithm squeezes THIS card shorter than
          its max-h-80 content instead of letting the outer container scroll —
          the inner list still scrolls to its own end, but the last rows render
          past the card's clipped, shrunk bottom edge and are never reachable.
          flex-shrink-0 keeps the card at its natural (up to max-h-80) height so
          any overflow is handled by the outer scroll instead of clipping it. */}
      <div className="w-full flex-shrink-0 border border-border/50 dark:border-white/[0.08] rounded-lg overflow-hidden">
        <div className="max-h-80 overflow-y-auto overflow-x-hidden" style={{ scrollbarGutter: 'stable' }}>
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <DotLoader size="md" label="Loading contacts" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-10 text-center px-6">
            <p className="text-sm text-muted-foreground">
              {contacts.length === 0
                ? 'No contacts found. Import contacts before running the playground.'
                : 'No contacts match your search.'}
            </p>
            {/* §24 — a filtered empty state must not hide the one action that
                resolves it. Only offered when a search is what emptied the list. */}
            {contacts.length > 0 && (
              <Button variant="outline" size="sm" className="mt-4" onClick={() => setSearch('')}>
                Clear search
              </Button>
            )}
          </div>
        ) : (
          /* Every row is its own grid on the SAME column template, so the
             briefcase + building icons still line up across rows: the first
             track is `auto` over a fixed-width avatar (16 + 36 + 12px in every
             row) and the rest are `minmax(0, …fr)` of an identical row width,
             so the tracks resolve identically. Each field truncates with an
             ellipsis when long and shows in full when it fits.

             This was one shared grid with `display: contents` rows. That gave
             the row button no box of its own, so it could not render a focus
             indicator — and it carried `focus:outline-none`, which removed the
             native one. A keyboard user had zero indication of which contact
             was selected. */
          <>
            {filtered.map((contact, idx) => {
              const name = resolveDisplayName(contact)
              const border = idx < filtered.length - 1 ? 'border-b border-border/50 dark:border-white/[0.06]' : ''
              const cell = 'flex items-center py-3'
              return (
                <button
                  key={contact.id}
                  type="button"
                  onClick={() => onSelect(contact.id, name)}
                  style={{ gridTemplateColumns: 'auto minmax(0,1.6fr) minmax(0,1fr) minmax(0,0.6fr)' }}
                  className={`grid w-full items-center text-left transition-colors hover:bg-muted/40 dark:hover:bg-white/[0.03] focus:outline-none focus-visible:bg-muted/40 dark:focus-visible:bg-white/[0.03] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#7170ff]/55 dark:focus-visible:ring-[#7170ff]/75 ${border}`}
                >
                  {/* Avatar */}
                  <div className={`${cell} pl-4 pr-3`}>
                    <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 bg-[#5e6ad2]/10 text-[#5e6ad2] dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc]">
                      <span className="text-sm font-medium">{initialsOf(name, contact.email)}</span>
                    </div>
                  </div>
                  {/* Name + email — truncate with … when long, full when they fit */}
                  <div className="flex flex-col justify-center min-w-0 py-3 pr-6">
                    <span className="truncate text-sm font-medium text-foreground">{name}</span>
                    <span className="truncate text-xs text-muted-foreground">{contact.email}</span>
                  </div>
                  {/* Role — truncates */}
                  <div className={`${cell} gap-1.5 min-w-0 pr-5 text-xs text-muted-foreground`}>
                    <Briefcase className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">{contact.job_title || '—'}</span>
                  </div>
                  {/* Company — truncates */}
                  <div className={`${cell} gap-1.5 min-w-0 pr-4 text-xs text-muted-foreground`}>
                    <Buildings className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">{contact.company_name || '—'}</span>
                  </div>
                </button>
              )
            })}
          </>
        )}
        </div>
      </div>
    </div>
  )
}
